<script setup lang="ts">
    import { LoaderCircle, Search } from 'lucide-vue-next';
    import { Input } from '@/shared/ui/molecules/Input';
    import { useCommandContext } from './context';

    withDefaults(
        defineProps<{
            /** Shows a spinner while results load. Unlike Input's `loading`, the input stays enabled, so focus and typing stay in it */
            loading?: boolean;
        }>(),
        { loading: false },
    );

    defineOptions({ inheritAttrs: false });

    const slots = defineSlots<{
        /** Replaces the search icon */
        prefix?(): unknown;
        suffix?(): unknown;
    }>();

    const { t } = useI18n();
    const attrs = useAttrs();
    const context = useCommandContext('CommandInput');
    const field = useTemplateRef('field');

    /* Named "Search" unless the consumer names it. */
    const ariaLabel = computed(() => (attrs['aria-label'] || attrs['aria-labelledby'] ? attrs['aria-label'] : t('SEARCH')));

    defineExpose({ focus: () => field.value?.focus() });
</script>

<template>
    <Input
        v-bind="$attrs"
        ref="field"
        class="ui-command-input"
        :model-value="context.search.value"
        type="text"
        role="combobox"
        autocomplete="off"
        spellcheck="false"
        aria-autocomplete="list"
        :aria-label="ariaLabel"
        :aria-expanded="context.hasVisibleItems.value"
        :aria-controls="context.hasVisibleItems.value ? context.listId : undefined"
        :aria-activedescendant="context.activeId.value"
        @update:model-value="(text: string) => context.setSearch(text)"
        @keydown="context.onKeydown"
    >
        <template #prefix>
            <slot name="prefix">
                <Search aria-hidden="true" />
            </slot>
        </template>
        <template
            v-if="slots.suffix || loading"
            #suffix
        >
            <LoaderCircle
                v-if="loading"
                class="ui-command-input__spinner"
                aria-hidden="true"
            />
            <slot name="suffix" />
        </template>
    </Input>
</template>

<style scoped>
    @layer components {
        .ui-command-input {
            flex: none;
            border-bottom: 1px solid var(--color-border);
        }

        /* The Command is the field's box: the input draws no border, shadow or focus ring of its own, since focus stays in it the whole time. */
        .ui-command-input :deep(.ui-input),
        .ui-command-input :deep(.ui-input:has(.ui-input__control:focus-visible)) {
            border-color: transparent;
            border-radius: 0;
            background-color: transparent;
            box-shadow: none;
        }

        .ui-command-input__spinner {
            animation: ui-command-input-spin 1s linear infinite;
        }

        @media (prefers-reduced-motion: reduce) {
            .ui-command-input__spinner {
                animation-duration: 3s;
            }
        }

        @keyframes ui-command-input-spin {
            to {
                transform: rotate(360deg);
            }
        }
    }
</style>

<i18n lang="yaml">
en:
    SEARCH: Search
nl:
    SEARCH: Zoeken
</i18n>
